import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import { useTeam } from '../contexts/TeamContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Calendar, Users, Heart, Clock, Briefcase } from 'lucide-react';

const API_URL = window.location.origin;

const Caregiving = () => {
  const { currentTeam } = useTeam();
  const [shifts, setShifts] = useState([]);
  const [careLogs, setCareLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  if (!currentTeam) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto fade-in">
          <h1 className="text-4xl font-bold mb-6 text-gray-900">Caregiving</h1>
          <Card className="p-8">
            <p className="text-gray-600">Please select a team to view caregiving information.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in">
        {/* Header with Quick Actions */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900">Caregiving</h1>
          <QuickActions position="top" />
        </div>

        <Tabs defaultValue="shifts" className="space-y-6">
          <TabsList>
            <TabsTrigger value="shifts">
              <Calendar className="w-4 h-4 mr-2" />
              Shifts
            </TabsTrigger>
            <TabsTrigger value="care-logs">
              <Heart className="w-4 h-4 mr-2" />
              Care Logs
            </TabsTrigger>
            <TabsTrigger value="projects">
              <Briefcase className="w-4 h-4 mr-2" />
              Projects
            </TabsTrigger>
          </TabsList>

          {/* Shifts Tab */}
          <TabsContent value="shifts">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Caregiving Shifts</h2>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Shift
                </Button>
              </div>
              <div className="text-center py-12 text-gray-900">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="font-medium">No shifts scheduled.</p>
                <p className="text-sm mt-2 text-gray-600">Schedule caregiving shifts and coordinate team coverage</p>
              </div>
            </Card>
          </TabsContent>

          {/* Care Logs Tab */}
          <TabsContent value="care-logs">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Daily Care Logs</h2>
                <LogCareDialog careLogs={careLogs} setCareLogs={setCareLogs} />
              </div>
              {careLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Heart className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No care logs yet.</p>
                  <p className="text-sm mt-2 text-gray-600">Log daily activities like meals, medications, mood, activities</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {careLogs.map(log => (
                    <Card key={log.id} className="p-4 border-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">{log.activity}</p>
                          <p className="text-sm text-gray-600 mt-1">{log.notes}</p>
                        </div>
                        <div className="text-sm text-gray-500">
                          {log.date} {log.time}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Caregiving Projects</h2>
                <CreateProjectDialog projects={projects} setProjects={setProjects} category="caregiving" />
              </div>
              {projects.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No projects yet.</p>
                  <p className="text-sm mt-2 text-gray-600">Track projects like "Find respite care", "Set up home care", "Research memory care"</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {projects.map(project => (
                    <Card key={project.id} className="p-4 border-2">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-lg text-gray-900">{project.name}</h3>
                        <Badge className="bg-blue-100 text-blue-900 border-blue-300">{project.status}</Badge>
                      </div>
                      <p className="text-gray-700">{project.description}</p>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Actions - Bottom */}
        <div className="mt-6">
          <QuickActions position="bottom" />
        </div>
      </div>
    </Layout>
  );
};

// Log Care Dialog
const LogCareDialog = ({ careLogs, setCareLogs }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    activity: '',
    notes: ''
  });

  const activities = [
    'Meal - Breakfast',
    'Meal - Lunch',
    'Meal - Dinner',
    'Medication Given',
    'Personal Care',
    'Exercise/Activity',
    'Medical Appointment',
    'Social Visit',
    'Other'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const newLog = {
      id: Date.now(),
      ...formData,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setCareLogs([...careLogs, newLog]);
    toast.success('Care logged successfully');
    setOpen(false);
    setFormData({ activity: '', notes: '' });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Log Care
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log Care Activity</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="activity" className="text-gray-900">Activity Type *</Label>
            <select
              id="activity"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.activity}
              onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
              required
            >
              <option value="">Select activity...</option>
              {activities.map(act => (
                <option key={act} value={act}>{act}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="notes" className="text-gray-900">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional details..."
              rows={3}
              className="text-gray-900 border-2"
            />
          </div>

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Log Activity
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Project Dialog
const CreateProjectDialog = ({ projects, setProjects, category }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'planning'
  });

  const projectTypes = {
    caregiving: [
      'Find Respite Care',
      'Set Up Home Care',
      'Research Memory Care Facilities',
      'Coordinate Medical Transportation',
      'Plan Caregiver Training',
      'Other'
    ]
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newProject = {
      id: Date.now(),
      ...formData,
      category,
      createdAt: new Date().toISOString()
    };
    setProjects([...projects, newProject]);
    toast.success('Project created successfully');
    setOpen(false);
    setFormData({ name: '', description: '', status: 'planning' });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Create Project
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Caregiving Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="project-name" className="text-gray-900">Project Type *</Label>
            <select
              id="project-name"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            >
              <option value="">Select project type...</option>
              {projectTypes[category].map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-900">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the project"
              rows={3}
              className="text-gray-900 border-2"
            />
          </div>

          <div>
            <Label htmlFor="status" className="text-gray-900">Status</Label>
            <select
              id="status"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="on-hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Create Project
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Caregiving;
